<template>
  <div
    class="q-pagination row items-center pagination-wrapper"
    :class="{
      'pagination-wrapper--centered': centered,
    }"
  >
    <!-- Per Page / Limit -->
    <div
      v-if="limits"
      class="row items-center pagination-left"
    >
      <span class="q-table__bottom-item">
        {{ $q.lang.table.recordsPerPage }}
      </span>
      <q-select
        :model-value="per_page"
        class="q-table__bottom-item q-table__select inline pagination-per-page"
        :options="limits"
        borderless
        dense
        options-dense
        options-cover
        @update:model-value="per_page_update"
      />
    </div>

    <div
      class="row items-center pagination-right"
      :class="{
        'pagination-right--centered': centered,
      }"
    >
      <!-- Records Index / Page Range -->
      <span class="q-table__bottom-item pagination-range">
        {{ $q.lang.table.pagination(page_range.first_item, page_range.last_item, page_range.total_count) }}
      </span>

      <!-- Page Links -->
      <div class="row items-center pagination-links">
        <template v-for="(link, key) in links">
          <!-- Gap: click to jump straight to a page number -->
          <div
            v-if="link.label === 'gap'"
            :key="key"
          >
            <q-btn
              flat
              dense
              no-caps
              text-color="grey-8"
              class="pagination-link pagination-link--gap"
              label="…"
            >
              <q-tooltip
                anchor="top middle"
                self="bottom middle"
              >
                {{ goto_i18n.tooltip }}
              </q-tooltip>
              <q-menu
                :model-value="goto_menu_key === key"
                anchor="bottom middle"
                self="top middle"
                @update:model-value="(opened) => goto_menu_toggle(opened, key)"
              >
                <div class="row items-center no-wrap q-pa-sm">
                  <q-input
                    v-model="goto_page"
                    type="number"
                    dense
                    outlined
                    autofocus
                    hide-bottom-space
                    :min="1"
                    :max="meta.last"
                    :placeholder="`1 - ${meta.last}`"
                    class="pagination-goto-input"
                    @keyup.enter="goto_submit"
                  />
                  <q-btn
                    dense
                    flat
                    round
                    icon="arrow_forward"
                    color="primary"
                    class="q-ml-xs"
                    :disable="!goto_valid"
                    @click="goto_submit"
                  />
                </div>
              </q-menu>
            </q-btn>
          </div>
          <div
            v-else
            :key="key + 'else2'"
          >
            <q-btn
              flat
              dense
              no-caps
              :round="!!link.icon"
              :disable="link.disabled"
              :icon="link.icon"
              :text-color="link.active ? 'primary' : 'grey-8'"
              class="pagination-link"
              :class="{
                'pagination-link--active': link.active,
                'pagination-link--icon': !!link.icon,
              }"
              @click="page_update(link)"
            >
              <template v-if="link.label">
                {{ link.label }}
              </template>
            </q-btn>
          </div>
        </template>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  components: {
  },
  props: {
    limits: {
      type: Array,
      required: false,
    },
    centered: {
      type: Boolean,
      default: false,
    },
    meta: {
      type: Object,
      required: true,
    },
    prop_keys: {
      type: Array,
      default: () => { return [] },
      required: false,
    },
  },
  emits: ['click'],
  data() {
    return {
      // Which gap ("…") has its jump-to-page menu open, and the typed page.
      goto_menu_key: null,
      goto_page: '',
    };
  },
  computed: {
    goto_i18n() {
      return {
        tooltip: this.$q.lang.isoName === 'fr' ? 'Aller à la page…' : 'Go to page…',
      };
    },
    goto_valid() {
      const page = parseInt(this.goto_page, 10);
      return Number.isInteger(page) && page >= 1 && page <= this.meta.last;
    },
    per_page() {
      return this.meta.limit;
    },
    page_range() {
      const per_page = this.meta.limit;
      const page_is_last = this.meta.page == this.meta.last;
      const first_item = ((this.meta.page - 1) * per_page) + 1;
      const total_count = this.meta.count;

      let last_item = 0;
      if (page_is_last) {
        last_item = total_count;
      } else {
        last_item = first_item + per_page - 1;
      }

      return {
        first_item: first_item,
        last_item: last_item,
        total_count: total_count,
      };
    },
    links() {
      return [
        {
          icon: this.$q.iconSet.pagination.first,
          url: this.url(1),
          page: 1,
          per_page: this.meta.limit,
          disabled: this.meta.page === 1,
        },
        {
          icon: this.$q.iconSet.pagination.prev,
          url: this.url(this.meta.prev),
          page: this.meta.prev,
          per_page: this.meta.limit,
          disabled: this.meta.page === 1,
        },
        ...this.meta.sequels['0'].map((page) => {
          return {
            label: page,
            url: this.url(page),
            page: page,
            per_page: this.meta.limit,
            active: this.active(page),
          };
        }),
        {
          icon: this.$q.iconSet.pagination.next,
          url: this.url(this.meta.next),
          page: this.meta.next,
          per_page: this.meta.limit,
          disabled: this.meta.page === this.meta.last,
        },
        {
          icon: this.$q.iconSet.pagination.last,
          url: this.url(this.meta.last),
          page: this.meta.last,
          per_page: this.meta.limit,
          disabled: this.meta.page === this.meta.last,
        },
      ];
    },
  },
  watch: {
  },
  methods: {
    goto_menu_toggle: function(opened, key) {
      this.goto_menu_key = opened ? key : null;
      if (opened) {
        this.goto_page = '';
      }
    },
    goto_submit: function() {
      if (!this.goto_valid) {
        return;
      }
      const page = parseInt(this.goto_page, 10);
      this.goto_menu_key = null;
      this.goto_page = '';
      this.page_update({
        url: this.url(page),
        page: page,
        per_page: this.meta.limit,
      });
    },
    per_page_update: function(limit) {
      const link = {
        url: this.url(this.meta.page, limit),
        per_page: limit,
        page: this.meta.page,
      };
      this.page_update(link);
    },
    page_update: function(link) {
      const url = link.url;
      const click_listener = !!this.$.vnode.props?.onClick;
      if (click_listener) {
        this.$emit('click', url, {
          page: link.page,
          per_page: link.per_page,
        });
      } else {
        let inertia_options = {
          preserveState: true,
          preserveScroll: true,
          replace: true,
        };
        if (this.prop_keys.length > 0) {
          inertia_options.only = this.prop_keys;
        }

        this.$inertia.get(
          url,
          {},
          inertia_options,
        );
      }
    },
    url: function(page = this.meta.page, limit = this.meta.limit) {
      let str = this.meta.scaffold_url;
      if (page) {
        str = str.replace(/__pagy_page__/, page);
      }
      if (limit) {
        str = str.replace(/__pagy_limit__/, limit);
      }
      return str;
    },
    active: function(page_number) {
      return this.meta.page == page_number;
    },
  },
};
</script>

<style scoped>
.pagination-wrapper {
  width: 100%;
  flex-wrap: wrap;
  row-gap: 8px;
}
.pagination-wrapper--centered {
  justify-content: center;
}
.pagination-left {
  column-gap: 8px;
  flex-wrap: wrap;
}
.pagination-right {
  margin-left: auto;
  column-gap: 8px;
  flex-wrap: wrap;
  justify-content: flex-end;
}
.pagination-right--centered {
  margin-left: 0;
  justify-content: center;
}
.pagination-links {
  column-gap: 4px;
  row-gap: 4px;
  flex-wrap: wrap;
}
.pagination-link {
  min-width: 2em;
  padding: 3px 6px;
  border-radius: 4px;
  box-shadow: none;
  background: transparent;
}
.pagination-link--gap {
  color: #6b7280;
}
.pagination-goto-input {
  width: 108px;
}
.pagination-link--icon {
  min-width: 32px;
  padding: 3px;
}
.pagination-link--active {
  font-weight: 600;
  background-color: var(--pagination-active-background, #fafafa);
}
.pagination-per-page :deep(.q-field__control) {
  background: transparent !important;
  box-shadow: none !important;
}
.pagination-per-page :deep(.q-field__control)::before {
  border: none !important;
}
</style>
